// ProgresoStreamingSection.js — Tablero de progreso del alumno (tipo
// `progreso_streaming`, página /contenidos de Learning). Se distingue de la
// portada en que todo gira alrededor de SU avance:
//   1. «Sigue donde quedaste»: la última lección que tocó, con un anillo grande
//      y Reanudar (el Mentor abre solo en la última lección vista).
//   2. Cifras de su recorrido.
//   3. Cada curso empezado como una serie: un cuadrito por lección agrupado por
//      módulo (vista / siguiente / pendiente). Es la pieza protagonista.
//   4. Los cursos que aún no empieza, al final.
// Los cursos salen del catálogo de la portada (catalogo_streaming) y el detalle
// de mentor/progreso-proyecto.php?detalle=1.
// Planes de formación: con subrol especial, solo sus cursos y una única sección «Mis cursos
// obligatorios» (se conservan «Sigue donde quedaste» y las cifras); Inactivo ve un aviso.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { mentor2Service, certificadoService, BACKEND_BASE } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import useMiPlan from '../../../hooks/useMiPlan';

const FUENTE = "'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
const urlImg = (v) => (!v ? '' : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));
const sinHtml = (t) => String(t || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

// «hoy», «ayer», «hace 3 días», «hace 2 semanas»…
const haceCuanto = (f) => {
  if (!f) return '';
  const d = new Date(String(f).replace(' ', 'T') + 'Z');
  const dias = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  if (dias < 14) return `hace ${dias} días`;
  if (dias < 60) return `hace ${Math.round(dias / 7)} semanas`;
  return `hace ${Math.round(dias / 30)} meses`;
};

const Play = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7.5 5v14l11.5-7Z" /></svg>;

const Raiz = styled.div`
  --acento: ${p => p.$acento};
  --fondo: ${p => p.$fondo};
  background: var(--fondo); color: #d5d9df; font-family: ${FUENTE}; min-height: 100vh;
`;

// ---------- 1. Sigue donde quedaste ----------
const Portada = styled.section`
  position: relative; isolation: isolate; overflow: hidden; min-height: clamp(520px, 78vh, 760px);
  display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: end; gap: 2rem 4rem;
  padding: clamp(8rem, 13vw, 10rem) 4vw clamp(2.5rem, 5vw, 4rem);
  > img { position: absolute; inset: 0; z-index: -2; width: 100%; height: 100%; object-fit: cover; }
  &::before { content: ''; position: absolute; inset: 0; z-index: -1;
    background:
      linear-gradient(to top, var(--fondo) 3%, color-mix(in srgb, var(--fondo) 70%, transparent) 38%, transparent 75%),
      linear-gradient(to right, var(--fondo) 0%, color-mix(in srgb, var(--fondo) 75%, transparent) 38%, transparent 70%); }
  .eyebrow { display: inline-flex; align-items: center; gap: .6rem; font-size: .95rem; font-weight: 600; color: #e7eaee; }
  .eyebrow::before { content: ''; width: 28px; height: 2px; background: var(--acento); }
  h1 { margin: .8rem 0 0; max-width: 16ch; font-size: clamp(2.6rem, 6.4vw, 5.6rem); font-weight: 800; font-stretch: 76%; line-height: .92; letter-spacing: -.02em; color: #fff; text-wrap: balance; }
  .donde { margin: 1.3rem 0 0; max-width: 40rem; font-size: 1.1rem; line-height: 1.5; color: #e7eaee; }
  .donde span { display: block; font-size: .92rem; color: #aab1bb; }
  .botones { display: flex; flex-wrap: wrap; gap: .7rem; margin-top: 1.8rem; }
  @media (max-width: 760px) { grid-template-columns: 1fr; min-height: 0; padding-top: 7rem; }
`;
const BotonPpal = styled(Link)`
  display: inline-flex; align-items: center; gap: .6rem; height: 54px; padding: 0 1.6rem; border-radius: 8px; text-decoration: none;
  background: #fff; color: #111; font-size: 1.05rem; font-weight: 700; transition: transform .15s ease, background .2s;
  svg { width: 20px; height: 20px; }
  &:hover { background: rgba(255,255,255,.86); }
  &:active { transform: scale(.98); }
  &:focus-visible { outline: 2px solid var(--acento); outline-offset: 3px; }
`;
const BotonSec = styled.a`
  display: inline-flex; align-items: center; gap: .5rem; height: 54px; padding: 0 1.4rem; border-radius: 8px; text-decoration: none;
  background: rgba(109,109,110,.6); color: #fff; font-size: 1.05rem; font-weight: 700; backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  &:hover { background: rgba(109,109,110,.4); }
  &:focus-visible { outline: 2px solid var(--acento); outline-offset: 3px; }
`;
const llenar = keyframes`from { stroke-dashoffset: var(--total); }`;
const AnilloGrande = styled.figure`
  position: relative; margin: 0; width: clamp(150px, 16vw, 210px); aspect-ratio: 1; justify-self: end;
  svg { width: 100%; height: 100%; transform: rotate(-90deg); }
  circle { fill: none; stroke-width: 7; }
  .base { stroke: rgba(255,255,255,.14); }
  .valor { stroke: var(--acento); stroke-linecap: round; stroke-dasharray: var(--total); stroke-dashoffset: var(--resta);
    animation: ${llenar} 1.4s .3s cubic-bezier(.3,.7,.2,1) both; filter: drop-shadow(0 0 10px color-mix(in srgb, var(--acento) 70%, transparent)); }
  figcaption { position: absolute; inset: 0; display: grid; place-content: center; text-align: center; }
  b { font-size: clamp(2.4rem, 4vw, 3.4rem); font-weight: 800; font-stretch: 80%; color: #fff; line-height: 1; font-variant-numeric: tabular-nums; }
  b small { font-size: .45em; font-weight: 700; color: #aab1bb; }
  span { margin-top: .3rem; font-size: .85rem; color: #aab1bb; }
  @media (max-width: 760px) { justify-self: start; width: 120px; order: -1; b { font-size: 2rem; } span { font-size: .75rem; } }
  @media (prefers-reduced-motion: reduce) { .valor { animation: none; } }
`;

// ---------- 2. Cifras ----------
const Cifras = styled.dl`
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); margin: 0 4vw; border-top: 1px solid rgba(255,255,255,.1); border-bottom: 1px solid rgba(255,255,255,.1);
  div { padding: 1.4rem 1.2rem 1.3rem 0; }
  div + div { padding-left: 1.4rem; border-left: 1px solid rgba(255,255,255,.1); }
  dd { margin: 0; font-size: clamp(2rem, 3.6vw, 2.8rem); font-weight: 800; font-stretch: 80%; line-height: 1; color: #fff; font-variant-numeric: tabular-nums; }
  dt { margin-top: .4rem; font-size: .9rem; color: #9aa1ab; }
  @media (max-width: 760px) { grid-template-columns: repeat(2, minmax(0, 1fr)); div:nth-child(3) { padding-left: 0; border-left: 0; } div:nth-child(n+3) { border-top: 1px solid rgba(255,255,255,.1); } }
`;

// ---------- 3. Recorrido por curso ----------
const Bloque = styled.section`
  padding: clamp(2rem, 4vw, 3rem) 4vw 0;
  > h2 { display: flex; align-items: baseline; gap: .6rem; margin: 0 0 .9rem; font-size: clamp(1.3rem, 2.2vw, 1.7rem); font-weight: 800; font-stretch: 85%; color: #fff; }
  > h2 small { font-size: .9rem; font-weight: 600; font-stretch: 100%; color: #8a929d; }
  > p { margin: 0 0 1.2rem; color: #8a929d; font-size: .95rem; }
`;
// Carrusel: una fila que se desliza con flechas (como las de la portada).
const Carril = styled.div`
  position: relative;
  .pista { display: grid; grid-auto-flow: column; grid-auto-columns: ${p => p.$ancho}; gap: .8rem; overflow-x: auto; scroll-snap-type: x mandatory;
    scrollbar-width: none; padding: .3rem 0 .6rem; }
  .pista::-webkit-scrollbar { display: none; }
  .pista > * { scroll-snap-align: start; display: flex; min-width: 0; }
  .pista > * > a { flex: 1; min-width: 0; }
  .flecha { position: absolute; top: 0; bottom: .6rem; z-index: 3; width: 3.2rem; display: grid; place-items: center; border: 0; cursor: pointer; color: #fff;
    opacity: 0; transition: opacity .2s; }
  .flecha svg { width: 30px; height: 30px; transition: transform .15s; }
  .flecha:hover svg { transform: scale(1.2); }
  .flecha.izq { left: -4vw; background: linear-gradient(to right, var(--fondo) 30%, transparent); }
  .flecha.der { right: -4vw; background: linear-gradient(to left, var(--fondo) 30%, transparent); }
  &:hover .flecha, .flecha:focus-visible { opacity: 1; }
  .flecha:focus-visible { outline: 2px solid var(--acento); outline-offset: -4px; }
  @media (hover: none) { .flecha { display: none; } }
`;
// Tarjeta resumida de un curso empezado.
const Tarjeta = styled(Link)`
  display: flex; flex-direction: column; border-radius: 8px; overflow: hidden; text-decoration: none; color: inherit;
  background: #1d1d1d; box-shadow: inset 0 0 0 1px rgba(255,255,255,.06); transition: transform .25s ease, box-shadow .25s ease;
  &:hover { transform: translateY(-4px); box-shadow: 0 22px 44px -22px #000, inset 0 0 0 1px rgba(255,255,255,.14); }
  &:focus-visible { outline: 2px solid var(--acento); outline-offset: 3px; }
  .img { position: relative; flex: none; aspect-ratio: 16 / 9; background: #222; overflow: hidden; }
  .img img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .img .play { position: absolute; inset: 0; display: grid; place-items: center; background: rgba(0,0,0,.35); opacity: 0; transition: opacity .2s; }
  .img .play span { display: grid; place-items: center; width: 52px; height: 52px; border-radius: 50%; background: rgba(0,0,0,.55); box-shadow: inset 0 0 0 2px #fff; color: #fff; }
  .img .play svg { width: 22px; height: 22px; margin-left: 3px; }
  &:hover .img .play, &:focus-visible .img .play { opacity: 1; }
  .pct { position: absolute; right: .55rem; top: .55rem; padding: .2rem .55rem; border-radius: 999px; background: rgba(0,0,0,.7); color: #fff;
    font-size: .8rem; font-weight: 800; font-variant-numeric: tabular-nums; }
  .pct.listo { background: #3ddc84; color: #06210f; }
  .cuerpo { display: flex; flex-direction: column; gap: .5rem; padding: .85rem .9rem 1rem; flex: 1; }
  h3 { margin: 0; font-size: 1rem; font-weight: 700; line-height: 1.25; color: #fff; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .sigue { margin: 0; font-size: .82rem; color: #9aa1ab; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sigue b { color: #e7eaee; font-weight: 600; }
  .lecciones { margin-top: auto; font-size: .78rem; color: #8a929d; font-variant-numeric: tabular-nums; }
`;
// Mapa resumido: una barra por módulo, llena según las lecciones vistas.
const Modulos = styled.div`
  display: flex; gap: 3px; margin-top: .15rem;
  i { position: relative; flex: 1; height: 7px; border-radius: 2px; background: rgba(255,255,255,.12); overflow: hidden; }
  i::after { content: ''; position: absolute; inset: 0 auto 0 0; width: var(--lleno); background: ${p => (p.$listo ? '#3ddc84' : 'var(--acento)')}; }
  i.actual { box-shadow: 0 0 0 1.5px #fff; }
`;
const IcoFlecha = ({ izq }) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={izq ? 'm15 5-7 7 7 7' : 'm9 5 7 7-7 7'} /></svg>;

// Carrusel con flechas que solo aparecen si hay a dónde moverse.
const Carrusel = ({ ancho, etiqueta, children }) => {
  const ref = useRef(null);
  const [lim, setLim] = useState({ izq: false, der: false });
  const medir = () => { const el = ref.current; if (!el) return; setLim({ izq: el.scrollLeft > 4, der: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 }); };
  useEffect(() => { medir(); window.addEventListener('resize', medir); return () => window.removeEventListener('resize', medir); });
  const mover = (dir) => { const el = ref.current; if (el) el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); };
  return (
    <Carril $ancho={ancho}>
      {lim.izq && <button type="button" className="flecha izq" onClick={() => mover(-1)} aria-label={`Anteriores: ${etiqueta}`}><IcoFlecha izq /></button>}
      <div className="pista" ref={ref} onScroll={medir} role="list" aria-label={etiqueta}>{children}</div>
      {lim.der && <button type="button" className="flecha der" onClick={() => mover(1)} aria-label={`Siguientes: ${etiqueta}`}><IcoFlecha /></button>}
    </Carril>
  );
};

// ---------- 4. Aún no empiezas ----------
const Poster = styled(Link)`
  position: relative; display: block; aspect-ratio: 16 / 9; border-radius: 6px; overflow: hidden; background: #222; text-decoration: none;
  &:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .35s; }
  &:hover img { transform: scale(1.05); }
  span { position: absolute; inset: auto 0 0; padding: 1.6rem .8rem .6rem; font-weight: 700; font-size: .95rem; line-height: 1.2; color: #fff;
    background: linear-gradient(to top, rgba(0,0,0,.9), transparent); }
`;
const Vacio = styled.p`margin: 0; padding: 2rem 0; color: #9aa1ab;`;
const Pie = styled.div`height: clamp(3rem, 6vw, 5rem);`;

const ProgresoStreamingSection = ({ proyecto }) => {
  const { user } = useAuth();
  const secs = proyecto?.config_json?.landing_secciones || [];
  const cat = secs.find(s => s.tipo === 'catalogo_streaming')?.config || {};
  const acento = cat.color_acento || '#E50914';
  const fondo = cat.color_fondo || '#141414';
  const docs = proyecto?.documentos;
  const mp = useMiPlan();
  const subrolLocal = user?.subrol || '';
  const modoPlan = mp.listo ? mp.modo : (['', 'sin-institucion'].includes(subrolLocal) ? 'todos' : 'esperando');

  const cursos = useMemo(() => {
    const vistos = new Set();
    const base = (cat.items || []).filter(it => it.documento_id && !vistos.has(String(it.documento_id)) && vistos.add(String(it.documento_id)));
    if (modoPlan === 'inactivo' || modoPlan === 'esperando') return [];
    // Plan: sus cursos en el orden del plan (los que no están en el catálogo, con los datos del documento).
    const fuente = modoPlan === 'plan'
      ? (mp.cursos || []).map(id => base.find(it => String(it.documento_id) === String(id)) || { documento_id: id })
      : base;
    return fuente.map(it => {
      const d = (docs || []).find(x => String(x.id) === String(it.documento_id));
      return {
        id: String(it.documento_id),
        titulo: it.titulo || d?.titulo || 'Curso',
        imagen: urlImg(it.imagen || d?.imagen),
        cartelera: urlImg(it.imagen_cartelera || it.imagen || d?.imagen),
        sinopsis: sinHtml(it.sinopsis || d?.descripcion),
      };
    });
  }, [cat.items, docs, modoPlan, mp.cursos]);

  const [datos, setDatos] = useState(null);
  const [certificados, setCertificados] = useState(null);
  const ids = cursos.map(c => c.id).join(',');
  useEffect(() => {
    if (!user || !ids) return undefined;
    let vivo = true;
    mentor2Service.progresoProyecto(ids.split(','), { detalle: true })
      .then(r => { if (vivo) setDatos(r.data || {}); })
      .catch(() => { if (vivo) setDatos({}); });
    certificadoService.miProgreso(proyecto?.id)
      .then(r => { if (vivo) setCertificados((r.data?.emitidos || []).filter(c => Number(c.proyecto_id) === Number(proyecto?.id)).length); })
      .catch(() => { if (vivo) setCertificados(0); });
    return () => { vivo = false; };
  }, [user, ids, proyecto]);

  const lista = useMemo(() => cursos.map(c => ({
    ...c,
    pct: Math.round(Number(datos?.progreso?.[c.id] || 0)),
    lec: datos?.lecciones?.[c.id] || { total: 0, hechas: 0 },
    det: datos?.detalle?.[c.id] || null,
  })), [cursos, datos]);

  if (!user) {
    return (
      <Raiz $acento={acento} $fondo={fondo}>
        <Bloque style={{ paddingTop: '9rem', paddingBottom: '6rem' }}>
          <h2>Tu progreso</h2>
          <p>Inicia sesión para ver tus cursos y seguir donde quedaste.</p>
          <BotonPpal to="/login?volver=/contenidos"><Play />Iniciar sesión</BotonPpal>
        </Bloque>
      </Raiz>
    );
  }

  if (modoPlan === 'esperando') return <Raiz $acento={acento} $fondo={fondo} />;
  if ((modoPlan === 'plan' || modoPlan === 'inactivo') && !cursos.length) {
    const inactivo = modoPlan === 'inactivo';
    return (
      <Raiz $acento={acento} $fondo={fondo}>
        <Bloque style={{ paddingTop: '9rem', paddingBottom: '6rem' }}>
          <h2>{inactivo ? 'Tu acceso está inactivo' : 'Aún no tienes cursos asignados'}</h2>
          <p>{inactivo
            ? 'Renueva tu suscripción o escríbenos para reactivarlo. Tu avance y tus certificados siguen guardados.'
            : 'Te avisaremos cuando estén disponibles los cursos de tu plan de formación.'}</p>
        </Bloque>
      </Raiz>
    );
  }
  const enPlan = modoPlan === 'plan';

  const empezados = lista.filter(c => c.pct > 0 || c.det?.ultima);
  const sinEmpezar = lista.filter(c => !(c.pct > 0 || c.det?.ultima));
  // El de la portada: el curso sin terminar que tocó más recientemente.
  const enMarcha = empezados.filter(c => c.pct < 100);
  const actual = [...enMarcha].sort((a, b) => String(b.det?.ultima?.fecha || '').localeCompare(String(a.det?.ultima?.fecha || '')))[0] || null;
  const destacado = actual || sinEmpezar[0] || lista[0] || null;
  const grupos = enPlan ? [
    { clave: 'obligatorios', titulo: 'Mis cursos obligatorios', cursos: [...enMarcha, ...sinEmpezar, ...empezados.filter(c => c.pct >= 100)] },
  ] : [
    { clave: 'casi', titulo: 'Casi terminas', texto: 'Te falta poco para completarlos.', cursos: enMarcha.filter(c => c.pct >= 75) },
    { clave: 'curso', titulo: 'En curso', texto: 'Cada cuadro es una lección: ábrela con un clic.', cursos: enMarcha.filter(c => c.pct < 75) },
    { clave: 'listo', titulo: 'Completados', texto: 'Puedes repasarlos cuando quieras.', cursos: empezados.filter(c => c.pct >= 100) },
  ].filter(g => g.cursos.length);
  const totalHechas = lista.reduce((n, c) => n + (c.lec?.hechas || 0), 0);
  const cargando = datos === null;

  // Lección siguiente: la que el Mentor abre al entrar sin parámetros es la
  // última vista; si ya la terminó, se lleva a la siguiente pendiente.
  const rutaReanudar = (c) => {
    const u = c.det?.ultima; const s = c.det?.siguiente;
    if (u && !u.hecha) return `/mentor/${c.id}`;
    if (s) return `/mentor/${c.id}?modulo=${s.modulo_id}&leccion=${s.n}`;
    return `/mentor/${c.id}`;
  };
  const textoDonde = (c) => {
    const u = c.det?.ultima; const s = c.det?.siguiente;
    const ref = u && !u.hecha ? u : (s || u);
    if (!ref) return null;
    return { leccion: ref.leccion, modulo: ref.modulo, cuando: u ? haceCuanto(u.fecha) : '' };
  };

  const R = 46; const TOTAL = 2 * Math.PI * R;
  const dondeActual = actual ? textoDonde(actual) : null;

  return (
    <Raiz $acento={acento} $fondo={fondo}>
      {destacado && (
        <Portada aria-labelledby="progreso-titulo">
          {destacado.cartelera && <img src={destacado.cartelera} alt="" />}
          <div>
            <span className="eyebrow">{actual ? 'Sigue donde quedaste' : 'Empieza tu primer curso'}</span>
            <h1 id="progreso-titulo">{destacado.titulo}</h1>
            {actual && dondeActual ? (
              <p className="donde">
                {dondeActual.leccion}
                <span>{dondeActual.modulo}{dondeActual.cuando ? `, la viste ${dondeActual.cuando}` : ''}</span>
              </p>
            ) : (
              destacado.sinopsis && <p className="donde" style={{ color: '#c3c9d1' }}>{destacado.sinopsis.slice(0, 180)}{destacado.sinopsis.length > 180 ? '…' : ''}</p>
            )}
            <div className="botones">
              <BotonPpal to={actual ? rutaReanudar(actual) : `/mentor/${destacado.id}`}><Play />{actual ? 'Reanudar' : 'Empezar'}</BotonPpal>
              {actual && <BotonSec href="#mis-cursos" onClick={e => { e.preventDefault(); document.getElementById('mis-cursos')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>Ver mis cursos</BotonSec>}
            </div>
          </div>
          {actual && (
            <AnilloGrande style={{ '--total': TOTAL, '--resta': TOTAL * (1 - actual.pct / 100) }}>
              <svg viewBox="0 0 100 100" aria-hidden="true"><circle className="base" cx="50" cy="50" r={R} /><circle className="valor" cx="50" cy="50" r={R} /></svg>
              <figcaption><b>{actual.pct}<small>%</small></b><span>{actual.lec.hechas} de {actual.lec.total} lecciones</span></figcaption>
            </AnilloGrande>
          )}
        </Portada>
      )}

      <Cifras aria-label="Tu recorrido" id="mis-cursos">
        <div><dd>{cargando ? '–' : totalHechas}</dd><dt>{totalHechas === 1 ? 'lección vista' : 'lecciones vistas'}</dt></div>
        <div><dd>{cargando ? '–' : enMarcha.length}</dd><dt>{enMarcha.length === 1 ? 'curso en marcha' : 'cursos en marcha'}</dt></div>
        <div><dd>{cargando ? '–' : empezados.filter(c => c.pct >= 100).length}</dd><dt>completados</dt></div>
        <div><dd>{certificados == null ? '–' : certificados}</dd><dt>{certificados === 1 ? 'certificado' : 'certificados'}</dt></div>
      </Cifras>

      {!enPlan && !cargando && !empezados.length && (
        <Bloque><h2>Tu recorrido</h2><Vacio>Cuando empieces un curso, aquí verás cada lección que llevas y cuál sigue.</Vacio></Bloque>
      )}

      {grupos.map(g => (
        <Bloque key={g.clave}>
          <h2>{g.titulo} <small>{g.cursos.length}</small></h2>
          <Carrusel ancho="clamp(240px, calc((100% - 3 * .8rem) / 4), 340px)" etiqueta={g.titulo}>
            {g.cursos.map(c => {
              const sg = c.det?.siguiente;
              const listo = c.pct >= 100;
              return (
                <div role="listitem" key={c.id}>
                  <Tarjeta to={listo ? `/mentor/${c.id}` : rutaReanudar(c)} aria-label={`${listo ? 'Repasar' : 'Continuar'} ${c.titulo}, ${c.pct}%`}>
                    <div className="img">
                      {c.imagen && <img src={c.imagen} alt="" loading="lazy" />}
                      <div className="play"><span><Play /></span></div>
                      <span className={`pct${listo ? ' listo' : ''}`}>{listo ? 'Completo' : `${c.pct}%`}</span>
                    </div>
                    <div className="cuerpo">
                      <h3>{c.titulo}</h3>
                      {!listo && sg && <p className="sigue">Sigue: <b>{sg.leccion}</b></p>}
                      {c.det?.modulos?.length > 0 && (() => {
                        // Siempre segmentada: una barra por módulo; si el curso
                        // tiene un solo módulo, una por lección.
                        const mods = c.det.modulos;
                        const segs = mods.length > 1
                          ? mods.map(m => {
                            const tot = m.lecciones.length; const h = m.lecciones.filter(l => l.hecha).length;
                            return { k: m.id, lleno: tot ? (h / tot) * 100 : 0, actual: sg && sg.modulo_id === m.id, titulo: `${m.titulo}: ${h}/${tot}` };
                          })
                          : mods[0].lecciones.map(l => ({ k: l.n, lleno: l.hecha ? 100 : 0, actual: sg && sg.modulo_id === mods[0].id && sg.n === l.n, titulo: l.titulo }));
                        return (
                          <Modulos $listo={listo} aria-hidden="true">
                            {segs.map(x => <i key={x.k} className={!listo && x.actual ? 'actual' : ''} style={{ '--lleno': `${x.lleno}%` }} title={x.titulo} />)}
                          </Modulos>
                        );
                      })()}
                      <span className="lecciones">{c.lec.hechas} de {c.lec.total} lecciones en {c.det?.modulos?.length || 1} {(c.det?.modulos?.length || 1) === 1 ? 'módulo' : 'módulos'}</span>
                    </div>
                  </Tarjeta>
                </div>
              );
            })}
          </Carrusel>
        </Bloque>
      ))}

      {!enPlan && sinEmpezar.length > 0 && !(sinEmpezar.length === 1 && destacado && !actual && sinEmpezar[0].id === destacado.id) && (
        <Bloque>
          <h2>Aún no empiezas <small>{sinEmpezar.filter(c => actual || c.id !== destacado?.id).length}</small></h2>
          <Carrusel ancho="clamp(200px, calc((100% - 4 * .8rem) / 5), 300px)" etiqueta="Aún no empiezas">
            {sinEmpezar.filter(c => actual || c.id !== destacado?.id).map(c => (
              <div role="listitem" key={c.id}><Poster to={`/mentor/${c.id}`}>{c.imagen && <img src={c.imagen} alt="" loading="lazy" />}<span>{c.titulo}</span></Poster></div>
            ))}
          </Carrusel>
        </Bloque>
      )}
      <Pie />
    </Raiz>
  );
};

export default ProgresoStreamingSection;
